import { ACCOUNT_CLOSED, ApiError, SESSION_EXPIRED, createClient } from "./http";
import type { ValidationDetail } from "./http";
import type {
  ActivityEvent,
  Analyze,
  AnalyzeCriterion,
  ApiKey,
  ApiKeyCreated,
  ChannelKind,
  DesktopReleases,
  Device,
  DeviceJournal,
  DeviceLogEntry,
  EnrollmentKey,
  EnrollmentKeyCreated,
  FilmstripSegment,
  Frame,
  LintReport,
  ModuleOut,
  Policy,
  PolicyDetail,
  Pool,
  Run,
  RunDetail,
  NotificationChannel,
  Scenario,
  ScenarioSummary,
  Subscription,
  TriggerConfig,
  TriggerScenario,
} from "./types";
import type {
  AgentBuild,
  LocalAgentBuild,
  Me,
  ReadyBuild,
  ScreenDesign,
  ScreenDesignAsset,
  ScreenDesignDetail,
} from "./types";
import type { Spec } from "../design/spec";

export { ACCOUNT_CLOSED, ApiError, SESSION_EXPIRED };
export type { ValidationDetail };

/**
 * Whether react-query should try a failed request again.
 *
 * A 401 has always been excluded: the token is gone, so the second attempt is
 * the same refusal. The three account-closed codes are the same situation one
 * level up — the account itself is shut, every retry is another 403, and they
 * only delay the moment the app can drop the session and say which of the
 * three it was.
 */
export function retryQuery(count: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.status === 401) return false;
    if (error.status === 403 && error.code && ACCOUNT_CLOSED.has(error.code)) return false;
  }
  return count < 2;
}

const TOKEN_KEY = "qa.token";

// --- the session ending under us --------------------------------------------
//
// Only the client sees the refusal — it arrives on whichever request happened
// to be in flight — and only the app can act on it, so the two are joined by a
// subscription rather than by every page learning to check for three codes.

type SessionEndedListener = (error: ApiError) => void;

const sessionEndedListeners = new Set<SessionEndedListener>();

/** Told when the server closes the account mid-session. Returns the unsubscribe. */
export function onSessionEnded(listener: SessionEndedListener): () => void {
  sessionEndedListeners.add(listener);
  return () => {
    sessionEndedListeners.delete(listener);
  };
}

const http = createClient({
  base: "/api/v1",
  tokenKey: TOKEN_KEY,
  onSessionEnded: (error) => {
    for (const listener of [...sessionEndedListeners]) listener(error);
  },
});

export function getToken(): string | null {
  return http.tokens.get();
}

export function setToken(token: string | null): void {
  http.tokens.set(token);
}

const request = http.request;

const post = http.post;

export const api = {
  login: async (email: string, password: string) => {
    const data = await post<{ access_token: string }>("/auth/login", { email, password });
    setToken(data.access_token);
    return data;
  },
  logout: () => setToken(null),
  /** Trade the admin console's one-time ticket for this tab's own session
   *  (`api/handoff.ts`). The far end re-checks everything the password door
   *  checks, so this refuses in exactly the ways `login` does. */
  consoleSession: async (ticket: string) => {
    const data = await post<{ access_token: string }>("/auth/console-session", { ticket });
    setToken(data.access_token);
    return data;
  },
  me: () => request<Me>("/auth/me"),

  devices: (params?: { status?: string; tag?: string[] }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set("status", params.status);
    params?.tag?.forEach((t) => query.append("tag", t));
    const suffix = query.toString();
    return request<Device[]>(`/devices${suffix ? `?${suffix}` : ""}`);
  },
  device: (id: string) => request<Device>(`/devices/${id}`),
  /** Journal tails for the whole fleet — one call, not one per device. */
  fleetJournal: (limit = 5) => request<DeviceJournal[]>(`/devices/journal?limit=${limit}`),
  /** Lab-wide activity feed for the dashboard. */
  events: (limit = 30) => request<ActivityEvent[]>(`/events?limit=${limit}`),
  deviceLogs: (id: string, limit = 50) =>
    request<DeviceLogEntry[]>(`/devices/${id}/logs?limit=${limit}`),
  updateDevice: (id: string, body: Partial<Device>) =>
    request<Device>(`/devices/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  quarantineDevice: (id: string, quarantined: boolean, reason?: string) =>
    post<Device>(`/devices/${id}/quarantine`, { quarantined, reason }),
  deviceAction: (id: string, cmd: string, args: Record<string, unknown> = {}) =>
    post<{ ok: boolean; data: unknown; error: string | null }>(`/devices/${id}/actions`, {
      cmd,
      args,
    }),
  /**
   * Ask one phone to run one of a module's commands (MODULES §8.3). The body
   * is the arguments object itself, not a wrapper: the form was generated from
   * the same manifest the server validates against, so there is nothing to
   * wrap it in. The answer is the ack's `data`.
   */
  deviceModuleCommand: (
    deviceId: string,
    module: string,
    command: string,
    args: Record<string, unknown> = {},
  ) => post<Record<string, unknown>>(`/devices/${deviceId}/modules/${module}/${command}`, args),
  triggerCaptures: (id: string) =>
    request<
      Array<{
        id: string;
        kind: string;
        s3_key: string;
        segment: string | null;
        captured_at_ms: number | null;
      }>
    >(`/devices/${id}/captures`),
  triggers: () => request<TriggerScenario[]>("/triggers"),
  createTrigger: (body: { name: string; description?: string; config: TriggerConfig }) =>
    post<TriggerScenario>("/triggers", body),
  updateTrigger: (
    id: string,
    body: { description?: string; config?: TriggerConfig },
  ) => request<TriggerScenario>(`/triggers/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteTrigger: (id: string) => request<void>(`/triggers/${id}`, { method: "DELETE" }),
  createEnrollmentToken: (tags: string[] = []) =>
    post<{ token: string; qr_payload: string; expires_at: string }>(
      "/devices/enrollment-tokens",
      { tags },
    ),

  /** Shared keys for APK builds: one build, every phone it lands on. */
  enrollmentKeys: () => request<EnrollmentKey[]>("/enrollment-keys"),
  createEnrollmentKey: (body: { name: string; max_devices?: number | null; tags?: string[] }) =>
    post<EnrollmentKeyCreated>("/enrollment-keys", body),
  revokeEnrollmentKey: (id: string) =>
    request<void>(`/enrollment-keys/${id}`, { method: "DELETE" }),

  pools: () => request<Pool[]>("/pools"),

  analyzes: () => request<Analyze[]>("/analyzes"),
  createAnalyze: (body: { name: string; criteria: AnalyzeCriterion[] }) =>
    post<Analyze>("/analyzes", body),
  updateAnalyze: (id: string, body: { name?: string; criteria?: AnalyzeCriterion[]; enabled?: boolean }) =>
    request<Analyze>(`/analyzes/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteAnalyze: (id: string) => request<void>(`/analyzes/${id}`, { method: "DELETE" }),

  // TODO(scenarios/runs/policies): the backend keeps these endpoints as 501
  // stubs while the features are reimplemented — the client surface below is
  // the contract they must come back against, so it stays.
  scenarios: () => request<ScenarioSummary[]>("/scenarios"),
  scenario: (id: string) => request<Scenario>(`/scenarios/${id}`),
  createScenario: (body: { name: string; yaml: string; description?: string }) =>
    post<Scenario>("/scenarios", body),
  updateScenario: (id: string, body: { yaml?: string; description?: string }) =>
    request<Scenario>(`/scenarios/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteScenario: (id: string) => request<void>(`/scenarios/${id}`, { method: "DELETE" }),
  lint: (yaml: string, policyId?: string) =>
    post<LintReport>("/scenarios/lint", { yaml, policy_id: policyId ?? null }),
  fromRecording: (body: unknown, save = false) =>
    post<{ yaml: string; lint: LintReport; saved_scenario_id: string | null }>(
      `/scenarios/from-recording?save=${save}`,
      body,
    ),

  runs: (params?: { status?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set("status", params.status);
    if (params?.limit) query.set("limit", String(params.limit));
    const suffix = query.toString();
    return request<Run[]>(`/runs${suffix ? `?${suffix}` : ""}`);
  },
  run: (id: string) => request<RunDetail>(`/runs/${id}`),
  startRun: (body: {
    scenario: string;
    devices?: Record<string, unknown>;
    policy?: string | null;
    params?: Record<string, unknown>;
  }) => post<Run>("/runs", { priority: "manual", ...body }),
  cancelRun: (id: string) => post<Run>(`/runs/${id}/cancel`),
  filmstrip: (jobId: string) =>
    request<{ frame_count: number; frames: Frame[]; segments: FilmstripSegment[] }>(
      `/artifacts/jobs/${jobId}/filmstrip`,
    ),
  artifactUrl: (id: string) => request<{ url: string }>(`/artifacts/${id}`),

  policies: () => request<Policy[]>("/policies"),
  policy: (id: string) => request<PolicyDetail>(`/policies/${id}`),
  createPolicy: (body: {
    name: string;
    description?: string;
    rules: Record<string, unknown>;
    allow_soft: boolean;
  }) => post<PolicyDetail>("/policies", body),

  secrets: () => request<string[]>("/secrets"),
  setSecret: (key: string, value: string) =>
    request<void>("/secrets", { method: "PUT", body: JSON.stringify({ key, value }) }),
  deleteSecret: (key: string) => request<void>(`/secrets/${key}`, { method: "DELETE" }),

  apiKeys: () => request<ApiKey[]>("/auth/api-keys"),
  createApiKey: (name: string) => post<ApiKeyCreated>("/auth/api-keys", { name }),
  revokeApiKey: (id: string) => request<void>(`/auth/api-keys/${id}`, { method: "DELETE" }),

  createPool: (name: string, description?: string) =>
    post<Pool>("/pools", { name, description }),

  channels: () => request<NotificationChannel[]>("/notifications/channels"),
  createChannel: (body: { name: string; kind: ChannelKind; config: Record<string, unknown> }) =>
    post<NotificationChannel>("/notifications/channels", body),
  deleteChannel: (id: string) =>
    request<void>(`/notifications/channels/${id}`, { method: "DELETE" }),
  testChannel: (id: string) =>
    post<{ ok: boolean; error?: string }>(`/notifications/channels/${id}/test`),

  // --- modules (MODULES §8.1, §8.2) ---------------------------------------
  /** The catalogue *is* the current release's manifests; `[]` when it carries
   *  none, which is what the Modules page says out loud. */
  modules: () => request<ModuleOut[]>("/modules"),
  setModuleEnabled: (id: string, enabled: boolean) =>
    request<ModuleOut>(`/modules/${id}`, { method: "PUT", body: JSON.stringify({ enabled }) }),

  // --- agent APK (B2B §8) --------------------------------------------------
  agentBuilds: () => request<AgentBuild[]>("/agent-builds"),
  agentBuild: (id: string) => request<AgentBuild>(`/agent-builds/${id}`),
  createAgentBuild: (body: {
    label?: string | null;
    enrollment_key_id?: string | null;
    max_devices?: number | null;
    design_id?: string | null;
    /** Left out — and so `null` on the wire — the build carries the tenant's
     *  enabled set (MODULES §8.4). Only a caller that wants exactly its own
     *  list sends one. */
    modules?: string[] | null;
  }) => post<AgentBuild>("/agent-builds", body),
  /** The same request, prepared for the desktop app to stamp and sign on this
   *  computer (DESKTOP §4.2). Refused for the same reasons `createAgentBuild`
   *  is refused, with the same codes — the desktop is not a way around them. */
  createLocalAgentBuild: (body: {
    label?: string | null;
    enrollment_key_id?: string | null;
    max_devices?: number | null;
    design_id?: string | null;
    modules?: string[] | null;
  }) => post<LocalAgentBuild>("/agent-builds/local", body),
  /** What the computer made of it (DESKTOP §4.3): the checksum and size of the
   *  file it wrote, or the sentence explaining why there is none. Every
   *  prepared build gets one, cancellations included. */
  reportLocalBuild: (
    id: string,
    body: { sha256: string; size: number } | { error: string },
  ) => post<AgentBuild>(`/agent-builds/${id}/local-result`, body),
  /** Where the finished APK actually lives, for a short while.
   *
   *  Asked for rather than linked to: the console authenticates with a bearer
   *  header, which a browser following an `<a href>` does not send, so a link
   *  straight at the API's `/download` is a 401 and no file. */
  agentBuildDownloadUrl: (id: string) =>
    request<{ url: string }>(`/agent-builds/${id}/download-url`),
  // --- screen designs (DESIGN §6) -----------------------------------------
  screenDesigns: () => request<ScreenDesign[]>("/screen-designs"),
  screenDesign: (id: string) => request<ScreenDesignDetail>(`/screen-designs/${id}`),
  createScreenDesign: (body: { name: string; spec?: Spec }) =>
    post<ScreenDesignDetail>("/screen-designs", body),
  updateScreenDesign: (id: string, body: { name?: string; spec: Spec; version: number }) =>
    request<ScreenDesignDetail>(`/screen-designs/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  duplicateScreenDesign: (id: string) =>
    post<ScreenDesignDetail>(`/screen-designs/${id}/duplicate`),
  setDefaultScreenDesign: (id: string) => post<ScreenDesign>(`/screen-designs/${id}/default`),
  deleteScreenDesign: (id: string) =>
    request<void>(`/screen-designs/${id}`, { method: "DELETE" }),
  uploadScreenDesignAsset: (id: string, file: File) => {
    const form = new FormData();
    form.set("file", file);
    return post<ScreenDesignAsset>(`/screen-designs/${id}/assets`, form);
  },
  deleteScreenDesignAsset: (id: string, assetId: string) =>
    request<void>(`/screen-designs/${id}/assets/${assetId}`, { method: "DELETE" }),

  // --- ready builds: the library of finished APKs --------------------------
  readyBuilds: () => request<ReadyBuild[]>("/ready-builds"),
  uploadReadyBuild: (file: File, notes?: string) => {
    const form = new FormData();
    form.set("file", file);
    if (notes?.trim()) form.set("notes", notes.trim());
    return post<ReadyBuild>("/ready-builds", form);
  },
  updateReadyBuild: (id: string, body: { notes?: string | null; visible_to_everyone?: boolean }) =>
    request<ReadyBuild>(`/ready-builds/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteReadyBuild: (id: string) => request<void>(`/ready-builds/${id}`, { method: "DELETE" }),
  /** Where the finished APK actually lives, for a short while.
   *
   *  Asked for rather than linked to: the console authenticates with a bearer
   *  header, which a browser following an `<a href>` does not send, so a link
   *  straight at the API's `/download` is a 401 and no file. */
  readyBuildDownloadUrl: (id: string) => request<{ url: string }>(`/ready-builds/${id}/download-url`),

  // --- the desktop app (DESKTOP §2.4) -------------------------------------
  //
  // Public, so the bearer this client adds is beside the point; it goes
  // through the same client anyway rather than a hand-rolled fetch.
  desktopReleases: () => request<DesktopReleases>("/desktop/releases"),

  subscriptions: () => request<Subscription[]>("/notifications/subscriptions"),
  createSubscription: (body: { channel_id: string; event: string }) =>
    post<Subscription>("/notifications/subscriptions", body),
  deleteSubscription: (id: string) =>
    request<void>(`/notifications/subscriptions/${id}`, { method: "DELETE" }),
};

export function websocketUrl(path: string): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const token = getToken();
  return `${protocol}//${window.location.host}${path}?token=${encodeURIComponent(token ?? "")}`;
}
